import { useState, useEffect } from 'react';
import {
    ClipboardCheck, Calendar, Save, X,
    AlertTriangle, CheckCircle, Search, Building2
} from 'lucide-react';

interface Equipment {
    id: string;
    equipmentNumber: string;
    equipmentType: string;
    locationDescription: string;
    facilityId: string;
}

interface Facility {
    id: string;
    name: string;
}

interface InspectionFormProps {
    onClose: () => void;
    onSave: () => void;
}

export default function InspectionForm({ onClose, onSave }: InspectionFormProps) {
    const [loading, setLoading] = useState(false);
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [equipmentList, setEquipmentList] = useState<Equipment[]>([]);

    // Form State
    const [selectedFacility, setSelectedFacility] = useState('');
    const [formData, setFormData] = useState({
        equipmentId: '',
        inspectionType: 'ANNUAL',
        inspectionDate: new Date().toISOString().split('T')[0],
        status: 'PASSED',
        notes: ''
    });

    useEffect(() => {
        fetchFacilities();
    }, []);

    useEffect(() => {
        if (selectedFacility) {
            fetchEquipment(selectedFacility);
        } else {
            setEquipmentList([]);
        }
    }, [selectedFacility]);

    const fetchFacilities = async () => {
        try {
            const res = await fetch('https://fire-safety-api.tedguy280.workers.dev/api/facilities', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await res.json();
            if (data.success) setFacilities(data.facilities);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchEquipment = async (facilityId: string) => {
        try {
            const res = await fetch(`https://fire-safety-api.tedguy280.workers.dev/api/facilities/${facilityId}/equipment`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await res.json();
            if (data.success) setEquipmentList(data.equipment);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const res = await fetch('https://fire-safety-api.tedguy280.workers.dev/api/inspections', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    equipmentId: formData.equipmentId,
                    facilityId: selectedFacility, // Optional but good for context
                    inspectionType: formData.inspectionType,
                    inspectionDate: new Date(formData.inspectionDate).getTime(),
                    status: formData.status,
                    notes: formData.notes
                })
            });

            const data = await res.json();
            if (data.success) {
                onSave();
            } else {
                alert('Failed: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Failed to create inspection:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="card-glass max-w-2xl w-full p-8 animate-scaleIn max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                    <h2 className="text-2xl font-display font-bold text-white flex items-center gap-3">
                        <ClipboardCheck className="w-8 h-8 text-ember-500" />
                        Log New Inspection
                    </h2>
                    <button onClick={onClose} className="text-steel-400 hover:text-white transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Facility Selection */}
                    <div>
                        <label className="label flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-steel-400" /> Facility
                        </label>
                        <select
                            required
                            className="input"
                            value={selectedFacility}
                            onChange={(e) => {
                                setSelectedFacility(e.target.value);
                                setFormData({ ...formData, equipmentId: '' }); // Reset equipment
                            }}
                        >
                            <option value="">Select Facility</option>
                            {facilities.map(f => (
                                <option key={f.id} value={f.id}>{f.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Equipment Selection - Only show if facility selected */}
                    {selectedFacility && (
                        <div>
                            <label className="label flex items-center gap-2">
                                <Search className="w-4 h-4 text-steel-400" /> Equipment
                            </label>
                            <select
                                required
                                className="input"
                                value={formData.equipmentId}
                                onChange={(e) => setFormData({ ...formData, equipmentId: e.target.value })}
                            >
                                <option value="">Select Equipment</option>
                                {equipmentList.map(item => (
                                    <option key={item.id} value={item.id}>
                                        {item.equipmentNumber} - {item.equipmentType} ({item.locationDescription})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="label">Inspection Type</label>
                            <select
                                className="input"
                                value={formData.inspectionType}
                                onChange={(e) => setFormData({ ...formData, inspectionType: e.target.value })}
                            >
                                <option value="ANNUAL">Annual Check</option>
                                <option value="VISUAL">Visual Check</option>
                                <option value="FIVE_YEAR">5-Year Inspection</option>
                                <option value="TEN_YEAR">10-Year Overhaul</option>
                            </select>
                        </div>
                        <div>
                            <label className="label">Date Performed</label>
                            <input
                                type="date"
                                required
                                className="input"
                                value={formData.inspectionDate}
                                onChange={(e) => setFormData({ ...formData, inspectionDate: e.target.value })}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="label">Status Result</label>
                        <div className="grid grid-cols-3 gap-3">
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, status: 'PASSED' })}
                                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${formData.status === 'PASSED'
                                    ? 'bg-safety-500/10 border-safety-500 text-safety-400'
                                    : 'border-steel-700 hover:border-steel-500 text-steel-400'
                                    }`}
                            >
                                <CheckCircle className="w-6 h-6" />
                                <span className="text-sm font-bold">Passed</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, status: 'ACTION_REQUIRED' })}
                                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${formData.status === 'ACTION_REQUIRED'
                                    ? 'bg-amber-500/10 border-amber-500 text-amber-400'
                                    : 'border-steel-700 hover:border-steel-500 text-steel-400'
                                    }`}
                            >
                                <AlertTriangle className="w-6 h-6" />
                                <span className="text-sm font-bold">Action Needed</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, status: 'FAILED' })}
                                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${formData.status === 'FAILED'
                                    ? 'bg-alert-500/10 border-alert-500 text-alert-400'
                                    : 'border-steel-700 hover:border-steel-500 text-steel-400'
                                    }`}
                            >
                                <X className="w-6 h-6" />
                                <span className="text-sm font-bold">Failed</span>
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="label">Notes / Observations</label>
                        <textarea
                            className="input min-h-[100px]"
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            placeholder="e.g. Pressure gauge replaced, seal broken..."
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-steel-700/30">
                        <button type="button" onClick={onClose} className="btn btn-secondary">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !formData.equipmentId}
                            className="btn btn-primary flex items-center gap-2"
                        >
                            {loading ? <div className="spinner w-4 h-4 border-2" /> : <Save className="w-4 h-4" />}
                            Save Log
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
